"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodesCryptoService = void 0;
const ttag_1 = require("ttag");
const interface_1 = require("../../interface");
const errors_1 = require("../../errors");
const errors_2 = require("../errors");
/**
 * Provides crypto operations for nodes metadata.
 *
 * The node crypto service is responsible for decrypting and encrypting node
 * metadata. It should export high-level actions only, such as "decrypt node"
 * instead of low-level operations like "decrypt node key". Low-level operations
 * should be kept private to the module.
 *
 * The service owns the logic to switch between old and new crypto model.
 */
class NodesCryptoService {
    driveCrypto;
    account;
    reporter;
    logger;
    constructor(telemetry, driveCrypto, account, reporter) {
        this.driveCrypto = driveCrypto;
        this.account = account;
        this.reporter = reporter;
        this.logger = telemetry.getLogger('nodes-crypto');
        this.driveCrypto = driveCrypto;
        this.account = account;
        this.reporter = reporter;
    }
    async decryptNode(node, parentKey) {
        const start = Date.now();
        const commonNodeMetadata = {
            ...node,
            encryptedCrypto: undefined,
        };
        const signatureEmailKeys = node.encryptedCrypto.signatureEmail
            ? await this.account.getPublicKeys(node.encryptedCrypto.signatureEmail)
            : [];
        // Parent key is node or share key. Anonymous files are signed with
        // the node parent key. If the anonymous file is shared directly,
        // there is no access to the parent key. In that case, the verification
        // is skipped.
        const nodeParentKeys = node.parentUid ? [parentKey] : [];
        // Anonymous uploads (without signature email set) use parent key instead.
        const keyVerificationKeys = node.encryptedCrypto.signatureEmail ? signatureEmailKeys : nodeParentKeys;
        let nameVerificationKeys;
        const nameSignatureEmail = node.encryptedCrypto.nameSignatureEmail;
        if (nameSignatureEmail === node.encryptedCrypto.signatureEmail) {
            nameVerificationKeys = keyVerificationKeys;
        }
        else {
            nameVerificationKeys = nameSignatureEmail
                ? await this.account.getPublicKeys(nameSignatureEmail)
                : nodeParentKeys;
        }
        // Start promises early, but await them only when required to do
        // as much work as possible in parallel.
        const [membershipPromise, namePromise, keyPromise] = [
            node.membership ? this.decryptMembership(node) : undefined,
            this.decryptName(node, parentKey, nameVerificationKeys),
            this.decryptKey(node, parentKey, keyVerificationKeys),
        ];
        let passphrase, key, passphraseSessionKey, keyAuthor;
        try {
            const keyResult = await keyPromise;
            passphrase = keyResult.passphrase;
            key = keyResult.key;
            passphraseSessionKey = keyResult.passphraseSessionKey;
            keyAuthor = keyResult.author;
        }
        catch (error) {
            void this.reporter.reportDecryptionError(node, 'nodeKey', error);
            const message = (0, errors_2.getErrorMessage)(error);
            const errorMessage = (0, ttag_1.c)('Error').t `Failed to decrypt node key: ${message}`;
            const { name, author: nameAuthor } = await namePromise;
            const membership = await membershipPromise;
            return {
                node: {
                    ...commonNodeMetadata,
                    name,
                    keyAuthor: (0, interface_1.resultError)({
                        claimedAuthor: getClaimedAuthor(node.encryptedCrypto.signatureEmail, keyVerificationKeys.length === 0),
                        error: errorMessage,
                    }),
                    nameAuthor,
                    membership,
                    activeRevision: 'file' in node.encryptedCrypto ? (0, interface_1.resultError)(new Error(errorMessage)) : undefined,
                    folder: undefined,
                    errors: [error],
                },
            };
        }
        const errors = [];
        let hashKey;
        let hashKeyAuthor;
        let folder;
        let folderExtendedAttributesAuthor;
        if ('folder' in node.encryptedCrypto) {
            const folderExtendedAttributesVerificationKeys = node.encryptedCrypto.signatureEmail
                ? signatureEmailKeys
                : [key];
            const [hashKeyPromise, folderExtendedAttributesPromise] = [
                this.decryptHashKey(node, key, signatureEmailKeys),
                this.decryptExtendedAttributes(node, node.encryptedCrypto.folder.armoredExtendedAttributes, key, folderExtendedAttributesVerificationKeys, node.encryptedCrypto.signatureEmail),
            ];
            try {
                const hashKeyResult = await hashKeyPromise;
                hashKey = hashKeyResult.hashKey;
                hashKeyAuthor = hashKeyResult.author;
            }
            catch (error) {
                void this.reporter.reportDecryptionError(node, 'nodeHashKey', error);
                errors.push(error);
            }
            try {
                const extendedAttributesResult = await folderExtendedAttributesPromise;
                folder = {
                    extendedAttributes: extendedAttributesResult.extendedAttributes,
                };
                folderExtendedAttributesAuthor = extendedAttributesResult.author;
            }
            catch (error) {
                void this.reporter.reportDecryptionError(node, 'nodeExtendedAttributes', error);
                errors.push(error);
            }
        }
        let activeRevision;
        let contentKeyPacketSessionKey;
        let contentKeyPacketAuthor;
        if ('file' in node.encryptedCrypto) {
            const [activeRevisionPromise, contentKeyPacketSessionKeyPromise] = [
                this.decryptRevision(node.uid, node.encryptedCrypto.activeRevision, key),
                this.driveCrypto.decryptAndVerifySessionKey(node.encryptedCrypto.file.base64ContentKeyPacket, node.encryptedCrypto.file.armoredContentKeyPacketSignature, key, 
                // Content key packet is signed with the node key, but
                // in the past some clients signed with the address key.
                [key, ...keyVerificationKeys]),
            ];
            try {
                activeRevision = (0, interface_1.resultOk)(await activeRevisionPromise);
            }
            catch (error) {
                void this.reporter.reportDecryptionError(node, 'nodeExtendedAttributes', error);
                const message = (0, errors_2.getErrorMessage)(error);
                const errorMessage = (0, ttag_1.c)('Error').t `Failed to decrypt active revision: ${message}`;
                activeRevision = (0, interface_1.resultError)(new Error(errorMessage));
            }
            try {
                const keySessionKeyResult = await contentKeyPacketSessionKeyPromise;
                contentKeyPacketSessionKey = keySessionKeyResult.sessionKey;
                contentKeyPacketAuthor =
                    keySessionKeyResult.verified !== undefined &&
                        (await this.reporter.handleClaimedAuthor(node, 'nodeContentKey', (0, ttag_1.c)('Property').t `content key`, keySessionKeyResult.verified, keySessionKeyResult.verificationErrors, node.encryptedCrypto.signatureEmail));
            }
            catch (error) {
                void this.reporter.reportDecryptionError(node, 'nodeContentKey', error);
                const message = (0, errors_2.getErrorMessage)(error);
                const errorMessage = (0, ttag_1.c)('Error').t `Failed to decrypt content key: ${message}`;
                contentKeyPacketAuthor = (0, interface_1.resultError)({
                    claimedAuthor: node.encryptedCrypto.signatureEmail,
                    error: errorMessage,
                });
                errors.push(error);
            }
        }
        // If key signature verificaiton failed, prefer returning error from
        // the key directly. If key signature is ok but not hash or folder
        // extended attributes, return that error instead. Only if all the
        // signatures using the same signature email are ok, return OK.
        let finalKeyAuthor;
        if (!keyAuthor.ok) {
            finalKeyAuthor = keyAuthor;
        }
        if (!finalKeyAuthor && contentKeyPacketAuthor && !contentKeyPacketAuthor.ok) {
            finalKeyAuthor = contentKeyPacketAuthor;
        }
        if (!finalKeyAuthor && hashKeyAuthor && !hashKeyAuthor.ok) {
            finalKeyAuthor = hashKeyAuthor;
        }
        if (!finalKeyAuthor && folderExtendedAttributesAuthor && !folderExtendedAttributesAuthor.ok) {
            finalKeyAuthor = folderExtendedAttributesAuthor;
        }
        if (!finalKeyAuthor) {
            finalKeyAuthor = keyAuthor;
        }
        const { name, author: nameAuthor } = await namePromise;
        const membership = await membershipPromise;
        const end = Date.now();
        const duration = end - start;
        this.logger.debug(`Node ${node.uid} decrypted in ${duration}ms`);
        return {
            node: {
                ...commonNodeMetadata,
                name,
                keyAuthor: finalKeyAuthor,
                nameAuthor,
                membership,
                activeRevision,
                folder,
                errors: errors.length ? errors : undefined,
            },
            keys: {
                passphrase,
                key,
                passphraseSessionKey,
                contentKeyPacketSessionKey,
                hashKey,
            },
        };
    }
    async decryptKey(node, parentKey, verificationKeys) {
        const key = await this.driveCrypto.decryptKey(node.encryptedCrypto.armoredKey, node.encryptedCrypto.armoredNodePassphrase, node.encryptedCrypto.armoredNodePassphraseSignature, [parentKey], verificationKeys);
        return {
            passphrase: key.passphrase,
            key: key.key,
            passphraseSessionKey: key.passphraseSessionKey,
            author: await this.reporter.handleClaimedAuthor(node, 'nodeKey', (0, ttag_1.c)('Property').t `key`, key.verified, key.verificationErrors, node.encryptedCrypto.signatureEmail, verificationKeys.length === 0),
        };
    }
    async decryptName(node, parentKey, verificationKeys) {
        const nameSignatureEmail = node.encryptedCrypto.nameSignatureEmail;
        try {
            const { name, verified, verificationErrors } = await this.driveCrypto.decryptNodeName(node.encryptedName, parentKey, verificationKeys);
            return {
                name: (0, interface_1.resultOk)(name),
                author: await this.reporter.handleClaimedAuthor(node, 'nodeName', (0, ttag_1.c)('Property').t `name`, verified, verificationErrors, nameSignatureEmail, verificationKeys.length === 0),
            };
        }
        catch (error) {
            void this.reporter.reportDecryptionError(node, 'nodeName', error);
            const errorMessage = (0, errors_2.getErrorMessage)(error);
            return {
                name: (0, interface_1.resultError)(new Error(errorMessage)),
                author: (0, interface_1.resultError)({
                    claimedAuthor: getClaimedAuthor(nameSignatureEmail, verificationKeys.length === 0),
                    error: errorMessage,
                }),
            };
        }
    }
    async getNameSessionKey(node, parentKey) {
        return this.driveCrypto.decryptSessionKey(node.encryptedName, parentKey);
    }
    async decryptMembership(node) {
        if (!node.membership) {
            return undefined;
        }
        let sharedBy;
        if (node.encryptedCrypto.membership) {
            let inviterEmailKeys;
            try {
                inviterEmailKeys = await this.account.getPublicKeys(node.encryptedCrypto.membership.inviterEmail);
            }
            catch (error) {
                this.logger.error('Failed to get inviter email keys', error);
                sharedBy = (0, interface_1.resultError)({
                    claimedAuthor: node.encryptedCrypto.membership.inviterEmail,
                    error: (0, ttag_1.c)('Error').t `Failed to get inviter keys`,
                });
            }
            try {
                const { verified, verificationErrors } = await this.driveCrypto.verifyInvitation(node.encryptedCrypto.membership.base64MemberSharePassphraseKeyPacket, node.encryptedCrypto.membership.armoredInviterSharePassphraseKeyPacketSignature, inviterEmailKeys || []);
                sharedBy = await this.reporter.handleClaimedAuthor(node, 'membershipInviter', (0, ttag_1.c)('Property').t `membership`, verified, verificationErrors, node.encryptedCrypto.membership.inviterEmail);
            }
            catch (error) {
                void this.reporter.reportVerificationError(node, 'membershipInviter');
                this.logger.error('Failed to verify invitation', error);
                sharedBy = (0, interface_1.resultError)({
                    claimedAuthor: node.encryptedCrypto.membership.inviterEmail,
                    error: (0, ttag_1.c)('Error').t `Failed to verify invitation`,
                });
            }
        }
        else {
            sharedBy = (0, interface_1.resultError)({
                error: (0, ttag_1.c)('Error').t `Missing inviter email`,
            });
        }
        return {
            role: node.membership.role,
            inviteTime: node.membership.inviteTime,
            sharedBy,
        };
    }
    async decryptHashKey(node, nodeKey, addressKeys) {
        if (!('folder' in node.encryptedCrypto)) {
            // This is developer error.
            throw new Error('Node is not a folder');
        }
        const { hashKey, verified, verificationErrors } = await this.driveCrypto.decryptNodeHashKey(node.encryptedCrypto.folder.armoredHashKey, nodeKey, addressKeys);
        return {
            hashKey,
            author: await this.reporter.handleClaimedAuthor(node, 'nodeHashKey', (0, ttag_1.c)('Property').t `hash key`, verified, verificationErrors, node.encryptedCrypto.signatureEmail),
        };
    }
    async decryptRevision(nodeUid, encryptedRevision, nodeKey) {
        const verificationKeys = encryptedRevision.signatureEmail
            ? await this.account.getPublicKeys(encryptedRevision.signatureEmail)
            : [nodeKey];
        const { extendedAttributes, author: contentAuthor } = await this.decryptExtendedAttributes({ uid: nodeUid, creationTime: encryptedRevision.creationTime }, encryptedRevision.armoredExtendedAttributes, nodeKey, verificationKeys, encryptedRevision.signatureEmail);
        return {
            uid: encryptedRevision.uid,
            state: encryptedRevision.state,
            creationTime: encryptedRevision.creationTime,
            storageSize: encryptedRevision.storageSize,
            contentAuthor,
            extendedAttributes,
            thumbnails: encryptedRevision.thumbnails,
        };
    }
    async decryptExtendedAttributes(node, encryptedExtendedAttributes, nodeKey, addressKeys, signatureEmail) {
        if (!encryptedExtendedAttributes) {
            return {
                author: (0, interface_1.resultOk)(signatureEmail),
            };
        }
        const { extendedAttributes, verified, verificationErrors } = await this.driveCrypto.decryptExtendedAttributes(encryptedExtendedAttributes, nodeKey, addressKeys);
        return {
            extendedAttributes,
            author: await this.reporter.handleClaimedAuthor(node, 'nodeExtendedAttributes', (0, ttag_1.c)('Property').t `attributes`, verified, verificationErrors, signatureEmail),
        };
    }
    async createFolder(parentKeys, address, name, extendedAttributes) {
        const { email, addressKey } = address;
        const [nodeKeys, { armoredNodeName }, hash] = await Promise.all([
            this.driveCrypto.generateKey([parentKeys.key], addressKey),
            this.driveCrypto.encryptNodeName(name, undefined, parentKeys.key, addressKey),
            this.driveCrypto.generateLookupHash(name, parentKeys.hashKey),
        ]);
        const { armoredHashKey, hashKey } = await this.driveCrypto.generateHashKey(nodeKeys.decrypted.key);
        const { armoredExtendedAttributes } = extendedAttributes
            ? await this.driveCrypto.encryptExtendedAttributes(extendedAttributes, nodeKeys.decrypted.key, addressKey)
            : { armoredExtendedAttributes: undefined };
        return {
            encryptedCrypto: {
                encryptedName: armoredNodeName,
                hash,
                armoredKey: nodeKeys.encrypted.armoredKey,
                armoredNodePassphrase: nodeKeys.encrypted.armoredPassphrase,
                armoredNodePassphraseSignature: nodeKeys.encrypted.armoredPassphraseSignature,
                folder: {
                    armoredExtendedAttributes: armoredExtendedAttributes,
                    armoredHashKey,
                },
                signatureEmail: email,
                nameSignatureEmail: email,
            },
            keys: {
                passphrase: nodeKeys.decrypted.passphrase,
                key: nodeKeys.decrypted.key,
                passphraseSessionKey: nodeKeys.decrypted.passphraseSessionKey,
                hashKey,
            },
        };
    }
    async encryptNewName(parentKeys, nodeNameSessionKey, address, newName) {
        const { email, addressKey } = address;
        const { armoredNodeName } = await this.driveCrypto.encryptNodeName(newName, nodeNameSessionKey, parentKeys.key, addressKey);
        const hash = parentKeys.hashKey
            ? await this.driveCrypto.generateLookupHash(newName, parentKeys.hashKey)
            : undefined;
        return {
            signatureEmail: email,
            armoredNodeName,
            hash,
        };
    }
    async encryptNodeWithNewParent(node, keys, parentKeys, address) {
        if (!parentKeys.hashKey) {
            throw new errors_1.ValidationError('Moving item to a non-folder is not allowed');
        }
        if (!node.name.ok) {
            throw new errors_1.ValidationError('Cannot move item without a valid name, please rename the item first');
        }
        const { email, addressKey } = address;
        const { armoredNodeName } = await this.driveCrypto.encryptNodeName(node.name.value, keys.nameSessionKey, parentKeys.key, addressKey);
        const hash = await this.driveCrypto.generateLookupHash(node.name.value, parentKeys.hashKey);
        const { armoredPassphrase, armoredPassphraseSignature } = await this.driveCrypto.encryptPassphrase(keys.passphrase, keys.passphraseSessionKey, [parentKeys.key], addressKey);
        return {
            encryptedName: armoredNodeName,
            hash,
            armoredNodePassphrase: armoredPassphrase,
            armoredNodePassphraseSignature: armoredPassphraseSignature,
            signatureEmail: email,
            nameSignatureEmail: email,
        };
    }
    async generateNameHashes(parentHashKey, names) {
        return Promise.all(names.map(async (name) => ({
            name,
            hash: await this.driveCrypto.generateLookupHash(name, parentHashKey),
        })));
    }
}
exports.NodesCryptoService = NodesCryptoService;
function getClaimedAuthor(claimedAuthor, notAvailableVerificationKeys = false) {
    if (!claimedAuthor && notAvailableVerificationKeys) {
        return null;
    }
    return claimedAuthor;
}
//# sourceMappingURL=cryptoService.js.map